import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithRedirect, encodePostRequest } from "features/api/utils/apiUtils.ts";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import {
	GetMerchandiseProductByIdResponse,
	GetMerchandiseProductByIdResponseData
} from "features/MarketingMaterials/api/getMerchandiseProductsById.ts";
import {
	GetShoppingCartResponse,
	GetShoppingCartResponseData,
	SubmitShoppingCartRequest,
	SubmitShoppingCartResponse,
	SubmitShoppingCartResponseData,
	UpdateShoppingCartRequest,
	ValidateOrderDetailsRequest,
	ValidateOrderDetailsResponse,
	ValidateOrderDetailsResponseData,
	ValidateShoppingCartResponse,
	ValidateShoppingCartResponseData
} from "features/MarketingMaterials/api/shoppingCart.ts";
import {
	MerchandiseProductFilterRequest,
	MerchandiseProductViewModelPagedItemCollection,
	MerchandiseProductViewModelPagedItemCollectionApiResponse
} from "./getMerchandiseProducts.ts";
import { GetMerchandiseFiltersResponse, GetMerchandiseFiltersResponseData } from "./getMerchandiseFilters.ts";
import {
	CartTemplateRequest,
	ShoppingCartTemplateDeleteResponse,
	ShoppingCartTemplateResponse,
	ShoppingCartTemplateResponseData
} from "features/MarketingMaterials/api/shoppingCartTemplates.ts";

export const merchandiseProductsApiSlice = createApi({
	reducerPath: "merchandiseProducts",
	baseQuery: baseQueryWithRedirect(baseURL),
	tagTypes: ["MerchandiseProducts", "ShoppingCart", "ShoppingCartTemplates"],
	endpoints: (builder) => ({
		getMerchandiseProducts: builder.query<
			MerchandiseProductViewModelPagedItemCollection | undefined,
			MerchandiseProductFilterRequest
		>({
			query: (request) => ({
				url: endpoints.marketingMaterials.getMerchandiseProducts(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(request)
			}),
			transformResponse: (response: MerchandiseProductViewModelPagedItemCollectionApiResponse) => response?.data,
			providesTags: ["MerchandiseProducts"]
		}),
		getMerchandiseProductById: builder.query<GetMerchandiseProductByIdResponseData, { productId: string }>({
			query: ({ productId }) => endpoints.marketingMaterials.getMerchandiseProductsById(productId),
			transformResponse: (response: GetMerchandiseProductByIdResponse) => response?.data
		}),
		getShoppingCart: builder.query<GetShoppingCartResponseData, void>({
			query: () => endpoints.marketingMaterials.shoppingCart(),
			transformResponse: (response: GetShoppingCartResponse) => response?.data,
			providesTags: ["ShoppingCart"]
		}),
		getMerchandiseFilters: builder.query<GetMerchandiseFiltersResponseData, void>({
			query: () => endpoints.marketingMaterials.getMerchandiseFilters(),
			transformResponse: (response: GetMerchandiseFiltersResponse) => response?.data
		}),
		updateShoppingCart: builder.mutation<GetShoppingCartResponseData, UpdateShoppingCartRequest>({
			query: (addToCartRequest) => ({
				url: endpoints.marketingMaterials.shoppingCart(),
				method: "PUT",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(addToCartRequest)
			}),
			transformResponse: (response: GetShoppingCartResponse) => response.data,
			invalidatesTags: ["ShoppingCart"]
		}),
		validateShoppingCart: builder.mutation<ValidateShoppingCartResponseData, { accountId?: string | null }>({
			query: ({ accountId }) => ({
				url: endpoints.marketingMaterials.validateShoppingCart(accountId),
				method: "POST"
			}),
			transformResponse: (response: ValidateShoppingCartResponse) => response?.data
		}),
		validateOrderDetails: builder.mutation<ValidateOrderDetailsResponseData, ValidateOrderDetailsRequest>({
			query: (request) => ({
				url: endpoints.marketingMaterials.validateOrderDetails(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: request
			}),
			transformResponse: (response: ValidateOrderDetailsResponse) => response?.data
		}),
		emptyShoppingCart: builder.mutation({
			query: () => ({
				url: endpoints.marketingMaterials.shoppingCart(),
				method: "DELETE"
			}),
			invalidatesTags: ["ShoppingCart"]
		}),
		getCartTemplates: builder.query<ShoppingCartTemplateResponseData, void>({
			query: () => endpoints.marketingMaterials.shoppingCartTemplates(),
			transformResponse: (response: ShoppingCartTemplateResponse) => response?.data,
			providesTags: ["ShoppingCartTemplates"]
		}),
		loadCartTemplate: builder.mutation<GetShoppingCartResponseData, { templateId: string }>({
			query: ({ templateId }) => ({
				url: endpoints.marketingMaterials.shoppingCartTemplates(templateId),
				method: "GET"
			}),
			transformResponse: (response: GetShoppingCartResponse) => response?.data,
			invalidatesTags: ["ShoppingCartTemplates", "ShoppingCart"]
		}),
		saveCartTemplate: builder.mutation<GetShoppingCartResponseData, CartTemplateRequest>({
			query: (request: CartTemplateRequest) => ({
				url: endpoints.marketingMaterials.shoppingCartTemplates(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(request)
			}),
			transformResponse: (response: GetShoppingCartResponse) => response?.data,
			invalidatesTags: ["ShoppingCartTemplates"]
		}),
		deleteShoppingCartTemplate: builder.mutation<ShoppingCartTemplateDeleteResponse, { templateId: string }>({
			query: ({ templateId }) => ({
				url: endpoints.marketingMaterials.shoppingCartTemplates(templateId),
				method: "DELETE"
			}),
			invalidatesTags: ["ShoppingCartTemplates"]
		}),
		submitShoppingCartOrder: builder.mutation<SubmitShoppingCartResponseData, SubmitShoppingCartRequest>({
			query: (request) => ({
				url: endpoints.marketingMaterials.shoppingCart(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(request)
			}),
			transformResponse: (response: SubmitShoppingCartResponse) => response?.data,
			invalidatesTags: ["ShoppingCart"]
		})
	})
});

export const {
	useLazyGetMerchandiseProductsQuery,
	useGetMerchandiseProductByIdQuery,
	useGetShoppingCartQuery,
	useUpdateShoppingCartMutation,
	useLazyGetMerchandiseFiltersQuery,
	useGetMerchandiseFiltersQuery,
	useValidateShoppingCartMutation,
	useValidateOrderDetailsMutation,
	useEmptyShoppingCartMutation,
	useLazyGetCartTemplatesQuery,
	useDeleteShoppingCartTemplateMutation,
	useLoadCartTemplateMutation,
	useSaveCartTemplateMutation,
	useSubmitShoppingCartOrderMutation
} = merchandiseProductsApiSlice;
